import { deburr } from 'lodash';
import { UITreeNode } from './types';

/**
 * Takes the given tree and returns a new tree with the empty groups removed
 * @param tree - tree to filter
 */
export const filterOutEmptyGroups = <T>(tree: UITreeNode<T>[]) => {
  const newTree: UITreeNode<T>[] = [];

  tree.forEach(node => {
    if (node.type !== 'group') {
      newTree.push(node);
    } else {
      if (node.children && node.children.length > 0 && hasChildOptionNode(node.children)) {
        newTree.push({
          ...node,
          children: filterOutEmptyGroups(node.children)
        });
      }
    }
  });

  return newTree;
};

/**
 * Takes a tree and removes any nodes that do not have the criteria in the label.
 * Please note that this will leave empty groups in the tree.
 * @param tree - tree to filter
 * @param criteria - criteria to filter by (on label)
 */
export const filterTreeByLabel = <T>(tree: UITreeNode<T>[], criteria: string) => {
  const newTree: UITreeNode<T>[] = [];
  const processedCriteria = deburr(criteria.toLowerCase());

  tree.forEach(node => {
    const processedLabel = deburr(node.label?.toLowerCase());
    if (node.type === 'option' && processedLabel.indexOf(processedCriteria) !== -1) {
      newTree.push(node);
    } else if (node.type === 'group') {
      newTree.push({
        ...node,
        children: node.children && node.children.length ? filterTreeByLabel(node.children, criteria) : undefined
      });
    }
  });

  return newTree;
};

/**
 * Will traverse the given tree to find the node with the given id
 * @param tree - tree to search (UITreeNode[])
 * @param id - id of the node to search for
 */
export const findNode = <T>(tree: UITreeNode<T>[], id: string) => {
  let foundNode: UITreeNode<T> | undefined;

  for (const node of tree) {
    if (node.id === id) {
      foundNode = node;
      break;
    }
    if (node.children?.length) {
      foundNode = findNode(node.children, id);
      if (foundNode) {
        break;
      }
    }
  }

  return foundNode;
};

/**
 * Generates and returns an index of nodes for the given tree
 * @param tree - Tree to generate a node index from
 * @param output - Output index. Do not specify this, used for recursion.
 */
export const generateIndexFromTreeNodes = <T>(tree: UITreeNode<T>[], output: Record<string, UITreeNode<T>> = {}) => {
  tree.forEach(node => {
    output[node.id] = node;
    if (node.type === 'group' && node.children) {
      generateIndexFromTreeNodes(node.children, output);
    }
  });
  return output;
};

/**
 * Filters the given tree by label based upon the given criteria
 * Populated groups are left intact so that the full hierarchy for a node will remain.
 * @param tree - Tree to filter
 * @param criteria - The criteria to filter the nodes by (case insensitive filter on label)
 */
export const getFilteredTree = <T>(tree: UITreeNode<T>[], criteria: string) => {
  if (!criteria.length) {
    return tree;
  }

  return filterOutEmptyGroups(filterTreeByLabel(tree, criteria));
};

/**
 * Searches the given tree to see if it has at least one child option node
 * @param tree - tree to search
 */
export const hasChildOptionNode = (tree: UITreeNode[]): boolean => {
  for (const node of tree) {
    if (node.type === 'option') {
      return true;
    } else {
      if (node.children && node.children.length) {
        return hasChildOptionNode(node.children);
      }
    }
  }

  return false;
};
